import React from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const AvatarRingWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  if (!state) return null;

  const { sessionAvatars, speakingAvatar, isAvatarsThinking, pendingContributions } = state;

  const getAvatarStatus = (role: string): 'speaking' | 'thinking' | 'pending' | 'idle' => {
    if (speakingAvatar === role) return 'speaking';
    if (isAvatarsThinking) return 'thinking';
    if (pendingContributions.some(c => c.role === role && c.state !== 'accepted' && c.state !== 'ignored')) return 'pending';
    return 'idle';
  };

  const ringColors = {
    speaking: 'border-primary shadow-[0_0_20px_rgba(0,199,196,0.4)]',
    thinking: 'border-amber-400 shadow-[0_0_16px_rgba(245,158,11,0.3)]',
    pending: 'border-primary/60',
    idle: isDark ? 'border-white/20' : 'border-slate-200',
  };

  const ringAnimations = {
    speaking: 'animate-pulse',
    thinking: 'animate-[pulse_2s_ease-in-out_infinite]',
    pending: '',
    idle: '',
  };

  return (
    <div className="flex items-center justify-center gap-6 h-full px-4">
      {sessionAvatars.map((avatar) => {
        const status = getAvatarStatus(avatar.role);
        let nameLabelClass: string;
        if (status === 'speaking') nameLabelClass = 'text-primary';
        else if (status === 'thinking') nameLabelClass = 'text-amber-400';
        else nameLabelClass = isDark ? 'text-white/60' : 'text-slate-500';
        return (
          <div key={avatar.role} className="flex flex-col items-center gap-2">
            {/* Avatar circle with status ring */}
            <div className="relative">
              <div className={`w-16 h-16 rounded-full border-[3px] overflow-hidden transition-all duration-500 ${ringColors[status]} ${ringAnimations[status]}`}>
                {avatar.imageUrl ? (
                  <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-xl font-bold"
                    style={{ backgroundColor: avatar.primaryColor || '#475569', color: '#fff' }}
                  >
                    {avatar.emoji || avatar.name.charAt(0)}
                  </div>
                )}
              </div>

              {/* Speaking indicator — sound wave bars */}
              {status === 'speaking' && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-end gap-[2px] h-3">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className="w-[3px] bg-primary rounded-full animate-sound-wave"
                      style={{ animationDelay: `${i * 0.15}s`, height: '40%' }}
                    />
                  ))}
                </div>
              )}

              {/* Thinking indicator — amber dot */}
              {status === 'thinking' && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
              )}
            </div>

            {/* Name label */}
            <span className={`text-xs font-medium truncate max-w-[80px] text-center ${nameLabelClass}`}>
              {avatar.name}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default AvatarRingWidget;
